import without from 'lodash/without';

import { CONTENTS } from '@lumapps/contents/keys';
import type { AttributeProps } from '@lumapps/lumx-blocks/components/Attributes/BaseAttribute';
import type { SortersType } from '@lumapps/lumx-filter-and-sort/types';
import { mdiTextBoxOutline, mdiCheckboxMarkedCircleOutline, mdiAlert } from '@lumapps/lumx/icons';
import { ColorPalette } from '@lumapps/lumx/react';
import { GLOBAL } from '@lumapps/translations';

export enum CONTENT_STATUS {
    DRAFT = 'DRAFT',
    PUBLISHED = 'PUBLISHED',
    UNPUBLISHED = 'UNPUBLISHED',
    ARCHIVED = 'ARCHIVE',
    DELETED = 'DELETED',
    /** Status LIVE will be changed to PUBLISHED when back will be implemented content statuses */
    LIVE = 'LIVE',
}

export enum PUBLICATION_STATUS {
    DRAFT = 'draft',
    PUBLISHED = 'published',
    SCHEDULED = 'scheduled',
    EXPIRED = 'expired',
    ARCHIVED = 'archived',
    DELETED = 'deleted',
}

export enum VALIDATION_STATUS {
    TO_VALIDATE = 'to_validate',
    REFUSED = 'refused',
}

export enum CONTENT_BULK_VALUES {
    UPDATED_AT = 'updatedAt',
    UPDATED_AT_REVERSE = '-updatedAt',
    TITLE_ASC = 'title',
    TITLE_DESC = '-title',
}

export enum CONTENT_BULK_ACTIONS {
    ARCHIVE = 'archive',
    UNARCHIVE = 'unarchive',
    PUBLISH = 'publish',
    UNPUBLISH = 'unpublish',
    DELETE = 'delete',
}

/** Imperative action labels for the bulk publication-status selector. */
export const CONTENT_BULK_ACTION_LABELS: Record<CONTENT_BULK_ACTIONS, string> = {
    [CONTENT_BULK_ACTIONS.PUBLISH]: GLOBAL.PUBLISH,
    [CONTENT_BULK_ACTIONS.UNPUBLISH]: GLOBAL.UNPUBLISH,
    [CONTENT_BULK_ACTIONS.ARCHIVE]: GLOBAL.ARCHIVE,
    [CONTENT_BULK_ACTIONS.UNARCHIVE]: GLOBAL.UNARCHIVE,
    [CONTENT_BULK_ACTIONS.DELETE]: GLOBAL.DELETE,
};

export const CONTENT_BULK_ACTION_OPTIONS = Object.values(CONTENT_BULK_ACTIONS);

export enum CONTENT_BULK_EDITION_STATUS {
    RUNNING = 'running',
    SUCCESS = 'success',
    ERROR = 'error',
    MISSING = 'missing',
}

export const SCOPE = 'all-content';
export const LIST_SCOPE = `${SCOPE}-list`;
export const LIST_FILTERS_SCOPE = `${LIST_SCOPE}-filters`;
export const LIST_BULK_SCOPE = `${SCOPE}-list-bulk`;
export const EDITION_DRAWER_SCOPE = `${LIST_BULK_SCOPE}-drawer`;
export const EDITION_DRAWER_CLASSNAME = 'content-bulk-edition-drawer';

export const BULK_LIST_CLASSNAME = 'content-bulk-list';

export const CONTENT_BULK_SORT_FIELDS: SortersType[] = [
    { label: GLOBAL.SORT_LATEST_UDPATE, id: CONTENT_BULK_VALUES.UPDATED_AT_REVERSE },
    { label: GLOBAL.SORT_OLDEST_UPDATE, id: CONTENT_BULK_VALUES.UPDATED_AT },
    {
        label: {
            key: GLOBAL.SORT_CRITERIA_ALPHABETICALLY_ASC,
            replacements: { sorting_criteria: GLOBAL.TITLE },
        },

        id: CONTENT_BULK_VALUES.TITLE_ASC,
    },
    {
        label: {
            key: GLOBAL.SORT_CRITERIA_ALPHABETICALLY_DESC,
            replacements: { sorting_criteria: GLOBAL.TITLE },
        },

        id: CONTENT_BULK_VALUES.TITLE_DESC,
    },
];

export const FALLBACK_CONTENT_ICON = mdiTextBoxOutline;

export const WorkflowValidationMap: Record<
    VALIDATION_STATUS,
    {
        label: string;
        icon: string;
        textColor?: { color?: AttributeProps['color']; colorVariant: AttributeProps['colorVariant'] };
    }
> = {
    to_validate: {
        label: CONTENTS.SUBMITTED_FOR_REVIEW_MESSAGE,
        icon: mdiCheckboxMarkedCircleOutline,
    },
    refused: {
        label: CONTENTS.REVISION_REQUESTED,
        textColor: { color: 'red', colorVariant: 'N' },
        icon: mdiAlert,
    },
};

export const PublicationStatusMap: Record<
    PUBLICATION_STATUS | CONTENT_BULK_ACTIONS,
    { color: ColorPalette; label: string }
> = {
    draft: { color: ColorPalette.yellow, label: GLOBAL.DRAFT },
    published: { color: ColorPalette.green, label: GLOBAL.PUBLISHED },
    publish: { color: ColorPalette.green, label: GLOBAL.PUBLISHED },
    unpublish: { color: ColorPalette.green, label: GLOBAL.UNPUBLISHED },
    scheduled: { color: ColorPalette.green, label: GLOBAL.SCHEDULED },
    expired: { color: ColorPalette.red, label: GLOBAL.EXPIRED },
    archived: { color: ColorPalette.dark, label: GLOBAL.ARCHIVED },
    archive: { color: ColorPalette.dark, label: GLOBAL.ARCHIVED },
    unarchive: { color: ColorPalette.dark, label: GLOBAL.UNARCHIVE },
    deleted: { color: ColorPalette.red, label: GLOBAL.DELETED },
    delete: { color: ColorPalette.red, label: GLOBAL.DELETED },
};

export const PUBLICATION_STATUS_FILTERS: PUBLICATION_STATUS[] = without(
    Object.values(PUBLICATION_STATUS),
    PUBLICATION_STATUS.DELETED,
);

export const PUBLICATION_STATUS_DEFAULT_FILTERS: PUBLICATION_STATUS[] = [
    PUBLICATION_STATUS.DRAFT,
    PUBLICATION_STATUS.EXPIRED,
    PUBLICATION_STATUS.PUBLISHED,
    PUBLICATION_STATUS.SCHEDULED,
];

export const BULK_OPERATION_ITEM_STATUS = {
    SUCCESSFUL: 'successful',
    FAILED: 'failed',
    NOT_RUNNED: 'notRun',
} as const;

export enum ADMIN_CONTENT_VIEWS {
    ALL_CONTENT = 'all-content',
    EDITORIAL_PLANNING = 'editorial-planning',
}

export const CONTENT_ANALYTICS_AUDITOR_AGENT_ID = 'fc878ad7-51d0-4a13-ad4a-ee70f24e72ba';

/** Dedicated feature flag gating the Content Analytics Auditor agent. */
export const CONTENT_ANALYTICS_AUDITOR_AGENT_FF = 'content-analytics-auditor-agent';
